import {
  AlertCard,
  Card,
  FileUploader,
  IconWrapper,
  Loader,
  TextInput,
} from "@caprikorn/dynamo-ui";
import { classNames } from "@caprikorn/dynamo-utils";
import { type FC, useId } from "react";
import { Controller } from "react-hook-form";

import { Meter } from "@icons";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./PodSerialCard.module.css";
import formClasses from "../form.module.css";
import type { PodSerialCardProps } from "./PodSerialCard.types";

export const PodSerialCard: FC<PodSerialCardProps> = ({
  isPending,
  isError,
  control,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const cardId = useId();

  return (
    <Card aria-labelledby={cardId} as="section" className={formClasses.card}>
      <h3
        className={classNames(formClasses.titleContainer, "font-title-md")}
        id={cardId}
      >
        <IconWrapper role="presentation" size="md" src={Meter} />
        {t("addPodDrawer.podSerial.title")}
      </h3>
      {isPending && (
        <div className={classes.loaderContainer}>
          <Loader />
          <span>{t("addPodDrawer.podSerial.isLoading")}</span>
        </div>
      )}
      <Controller
        control={control}
        name="podSerial"
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            className={formClasses.input}
            disabled={isPending}
            hasError={Boolean(fieldState.error)}
            hint={
              fieldState.error?.message &&
              t(fieldState.error.message, { ns: "common" })
            }
            label={t("addPodDrawer.podSerial.label")}
            size="lg"
          />
        )}
        rules={{ required: "form.error.required" }}
      />
      {isError && (
        <>
          <AlertCard variant="error">
            <AlertCard.Title>
              {t("addPodDrawer.podSerial.meterNotFound.title")}
            </AlertCard.Title>
            <AlertCard.Description>
              <p>{t("addPodDrawer.podSerial.meterNotFound.description1")}</p>
              <p>{t("addPodDrawer.podSerial.meterNotFound.description2")}</p>
            </AlertCard.Description>
          </AlertCard>
          <Controller
            control={control}
            name="attachments"
            render={({ field, fieldState }) => (
              <FileUploader
                {...field}
                accept="image/png, image/jpeg, image/jpg, .pdf"
                maxFileSize={10 * 1000 * 1000}
                value={field.value || []}
              >
                <FileUploader.Input
                  description={t("addPodDrawer.podSerial.uploader.description")}
                  hasError={Boolean(fieldState.error)}
                  hint={
                    fieldState.error?.message &&
                    t(fieldState.error.message, { ns: "common" })
                  }
                  label={t("addPodDrawer.podSerial.uploader.label")}
                />
                <FileUploader.Files />
              </FileUploader>
            )}
            rules={{ required: "form.error.fileRequired" }}
          />
        </>
      )}
    </Card>
  );
};
